import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    if (password !== confirm) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#EFE8D8] p-6">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md rounded-2xl border border-[#E7E2D3] bg-white p-8" data-testid="reset-password-page">
        {done ? (
          <div className="text-center">
            <CheckCircle2 className="h-12 w-12 text-emerald-500 mx-auto" />
            <h1 className="font-display text-2xl font-bold text-[#595C2B] mt-4">Password updated</h1>
            <p className="text-slate-500 mt-2">You can now log in with your new password.</p>
            <Button onClick={() => navigate("/login")} className="mt-6 rounded-full bg-[#595C2B] hover:bg-[#45481F] text-white" data-testid="reset-to-login-btn">Go to login</Button>
          </div>
        ) : !token ? (
          <div className="text-center">
            <h1 className="font-display text-2xl font-bold text-[#595C2B]">Invalid link</h1>
            <p className="text-slate-500 mt-2">This password reset link is missing or invalid.</p>
            <Link to="/login"><Button className="mt-6 rounded-full bg-[#595C2B] text-white">Back to login</Button></Link>
          </div>
        ) : (
          <>
            <div className="h-11 w-11 rounded-xl bg-[#595C2B]/10 flex items-center justify-center mb-4"><KeyRound className="h-5 w-5 text-[#595C2B]" /></div>
            <h1 className="font-display text-2xl font-bold text-[#595C2B]">Set a new password</h1>
            <p className="text-slate-500 mt-1 mb-6">Choose a strong new password for your account.</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="text-sm font-medium">New password</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1" data-testid="reset-password-input" />
              </div>
              <div>
                <Label className="text-sm font-medium">Confirm password</Label>
                <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="mt-1" data-testid="reset-confirm-input" />
              </div>
              <Button type="submit" disabled={loading} className="w-full rounded-full bg-[#595C2B] hover:bg-[#45481F] text-white btn-lift h-11" data-testid="reset-submit-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
              </Button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}
